import { PERMISSION_DEFINITIONS } from "@t3tools/contracts";

import { useAccess } from "../../access/AccessProvider";
import { useOperatorsAuth } from "../../operatorsAuth";

import { ThreadNotificationSettings } from "./ThreadNotificationSettings";
import { SettingsPageHeader, SettingsPanel, SettingsSection } from "./SettingsChrome";

const PERMISSION_NAME_BY_KEY = new Map<string, string>(
  PERMISSION_DEFINITIONS.map((entry) => [entry.key, entry.name]),
);

function formatRoleLabel(role: string): string {
  return role.replaceAll("_", " ");
}

function formatTimestamp(iso: string | null): string {
  if (!iso) {
    return "—";
  }
  const parsed = Date.parse(iso);
  if (!Number.isFinite(parsed)) {
    return iso;
  }
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsed);
}

function DetailRow(props: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
      <dt className="text-xs text-muted-foreground">{props.label}</dt>
      <dd className="min-w-0 break-all text-sm text-foreground sm:text-right">{props.value}</dd>
    </div>
  );
}

function ChipList(props: {
  empty: string;
  items: readonly string[];
  formatLabel?: (item: string) => string;
}) {
  if (props.items.length === 0) {
    return <p className="text-sm text-muted-foreground">{props.empty}</p>;
  }

  return (
    <ul className="flex flex-wrap gap-1.5">
      {props.items.map((item) => (
        <li
          key={item}
          className="rounded-md border border-border bg-background px-2 py-0.5 text-xs text-foreground"
        >
          {props.formatLabel?.(item) ?? item}
        </li>
      ))}
    </ul>
  );
}

export function AccountSettingsPanel() {
  const { expiresAt, user } = useOperatorsAuth();
  const { flags, isSuperAdmin, permissions, roles, tools } = useAccess();

  const sortedPermissions = [...permissions].sort((left, right) => left.localeCompare(right));
  const sortedFlagEntries = Object.entries(flags).sort(([left], [right]) =>
    left.localeCompare(right),
  );

  return (
    <SettingsPanel>
      <SettingsPageHeader
        title="Account"
        description="Your Operators account, notification preferences, and access details."
      />

      <SettingsSection title="Profile" description="Identity details from your current session.">
        {user ? (
          <dl className="space-y-3">
            <DetailRow label="Name" value={user.name || "—"} />
            <DetailRow label="Email" value={user.email || "—"} />
            <DetailRow label="Subject" value={user.subject || "—"} />
            <DetailRow label="Auth provider" value={user.authProvider || "—"} />
            <DetailRow label="Legacy role" value={user.role || "—"} />
            <DetailRow label="Session expires" value={formatTimestamp(expiresAt)} />
          </dl>
        ) : (
          <p className="text-sm text-muted-foreground">No signed-in user.</p>
        )}
      </SettingsSection>

      {user ? <ThreadNotificationSettings key={user.email} email={user.email} /> : null}

      <SettingsSection
        title="Access"
        description="Roles, permissions, tools, and flags resolved for your account."
      >
        <div className="space-y-5">
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Privilege</p>
            <p className="text-sm text-foreground">
              {isSuperAdmin ? "Super admin (bypasses permission checks)" : "Standard access"}
            </p>
          </div>

          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Roles</p>
            <ChipList empty="No roles granted." formatLabel={formatRoleLabel} items={[...roles]} />
          </div>

          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Permissions</p>
            {sortedPermissions.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {isSuperAdmin
                  ? "No explicit grants listed; super admin bypasses checks."
                  : "No permissions granted."}
              </p>
            ) : (
              <ul className="divide-y divide-border rounded-lg border border-border bg-background">
                {sortedPermissions.map((key) => (
                  <li
                    key={key}
                    className="flex flex-col gap-0.5 px-3 py-2 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4"
                  >
                    <span className="font-mono text-xs text-foreground">{key}</span>
                    <span className="text-xs text-muted-foreground sm:text-right">
                      {PERMISSION_NAME_BY_KEY.get(key) ?? "Custom permission"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Tools</p>
            {tools.length === 0 ? (
              <p className="text-sm text-muted-foreground">No tools visible in the sidebar.</p>
            ) : (
              <ul className="divide-y divide-border rounded-lg border border-border bg-background">
                {tools.map((tool) => (
                  <li
                    key={tool.slug}
                    className="flex flex-col gap-0.5 px-3 py-2 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4"
                  >
                    <span className="text-sm text-foreground">{tool.name}</span>
                    <span className="font-mono text-xs text-muted-foreground sm:text-right">
                      {tool.slug} · {tool.route}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Feature flags</p>
            {sortedFlagEntries.length === 0 ? (
              <p className="text-sm text-muted-foreground">No feature flags in this session.</p>
            ) : (
              <ul className="divide-y divide-border rounded-lg border border-border bg-background">
                {sortedFlagEntries.map(([key, enabled]) => (
                  <li key={key} className="flex items-center justify-between gap-4 px-3 py-2">
                    <span className="font-mono text-xs text-foreground">{key}</span>
                    <span className="text-xs text-muted-foreground">
                      {enabled ? "enabled" : "disabled"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </SettingsSection>
    </SettingsPanel>
  );
}
